import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import {
  categoryMeta,
  genreOptions,
  getVideosByCategory,
  yearOptions,
  type VideoCategory,
  type VideoItem,
} from "@/mocks/videos";
import FilterBar from "./components/FilterBar";
import { useSEO } from "@/hooks/useSEO";

const PAGE_SIZE = 8;

const isCategory = (v: string | undefined): v is VideoCategory =>
  v === "movie" || v === "tv" || v === "variety";

export default function Category() {
  const { categoryName } = useParams<{ categoryName: string }>();
  const [searchParams] = useSearchParams();
  const category: VideoCategory = isCategory(categoryName) ? categoryName : "movie";
  const meta = categoryMeta[category];

  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<"latest" | "hot">(
    searchParams.get("sort") === "hot" ? "hot" : "latest"
  );
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSEO({
    title: `${meta.name} - 粉色视频`,
    description: `粉色视频${meta.name}频道，${meta.intro}`,
    keywords: `${meta.name},粉色视频${meta.name},${genreOptions[category].slice(0, 4).join(",")},在线${meta.name}`,
  });

  useEffect(() => {
    setGenre("全部");
    setYear("全部");
    setVisible(PAGE_SIZE);
  }, [category]);

  const filtered = useMemo<VideoItem[]>(() => {
    let list = getVideosByCategory(category);
    if (genre !== "全部") list = list.filter((v) => v.genre.includes(genre));
    if (year !== "全部") list = list.filter((v) => v.year === Number(year));
    const sorted = [...list].sort((a, b) => {
      if (sort === "hot") return b.popularity - a.popularity;
      return a.updateDate < b.updateDate ? 1 : -1;
    });
    return sorted;
  }, [category, genre, year, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const reset = () => {
    setGenre("全部");
    setYear("全部");
    setSort("latest");
    setVisible(PAGE_SIZE);
  };

  const updateGenre = (v: string) => {
    setGenre(v);
    setVisible(PAGE_SIZE);
  };
  const updateYear = (v: string) => {
    setYear(v);
    setVisible(PAGE_SIZE);
  };
  const updateSort = (v: "latest" | "hot") => {
    setSort(v);
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full">
      <div className="bg-primary-50">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{meta.name}</span>
          </nav>
          <h1 className="mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
            {meta.name}频道
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.intro}</p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        <FilterBar
          genres={genreOptions[category]}
          years={yearOptions}
          activeGenre={genre}
          activeYear={year}
          sort={sort}
          onChangeGenre={updateGenre}
          onChangeYear={updateYear}
          onChangeSort={updateSort}
          onReset={reset}
        />

        <div className="mt-6 mb-5 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部{meta.name}
          </p>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
              {shown.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多（还有 {filtered.length - visible} 部）
                </button>
              </div>
            )}
            {!hasMore && (
              <p className="mt-10 text-center text-sm text-foreground-400">已经到底啦，换个筛选条件试试</p>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-800">
              抱歉，没有找到符合条件的视频
            </p>
            <p className="mt-2 text-sm text-foreground-500">试试更换类型或年份筛选条件</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}